import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { produce } from 'immer';
import { GuidelineService } from 'shared/domain';

import { ChapterWidget, ComponentExpandStrategy, GuidelineChapterType } from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { TitleDescriptionComponent } from './components/title-description/title-description.component';
import { DividerComponent } from '../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { GuidelineSidebarBasics, GuidelineSidebarBasicsComponent } from '../guideline-sidebar-basics/guideline-sidebar-basics.component';

@Component({
  standalone: true,
  imports: [
    MatTabsModule,
    TranslocoModule,
    TitleDescriptionComponent,
    GuidelineSidebarBasicsComponent,
    MatSelectModule,
    MatProgressSpinnerModule,
    FormsModule,
    DividerComponent,
  ],
  selector: 'guideline-editor-chapter-sidebar-wrapper',
  templateUrl: './guideline-editor-chapter-sidebar-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./guideline-editor-chapter-sidebar-wrapper.component.scss'],
})
export class GuidelineEditorChapterSidebarWrapperComponent {
  private guidelineService = inject(GuidelineService);

  protected widget = input.required<ChapterWidget>();

  protected setTitle(title: string): void {
    const updatedChapter = produce(this.widget(), (draft) => {
      draft.title = title;
    });
    this.guidelineService.updateWidget(this.widget().uniqueId, updatedChapter);
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    const newComponent = produce(this.widget(), (draft) => {
      draft.references = basics.references;
      draft.links = basics.links;
      draft.attachments = basics.attachments;
      draft.resourceTags = basics.resourceTags;
    });

    this.guidelineService.updateWidget(newComponent.uniqueId, newComponent);
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
  protected readonly GuidelineChapterType = GuidelineChapterType;
  protected readonly ComponentExpandStrategy = ComponentExpandStrategy;
}
